import * as A from '@core/arena'

import { Records, Dates } from '@openforis/arena-core'

import * as Validation from '@core/validation/validation'
import * as Node from '@core/record/node'

import { keys } from './recordKeys'
import * as RecordReader from './recordReader'

// ====== UPDATE

/**
 * Updates the record nodes with the ones passed as parameter.
 * Existing nodes will be replaced by the ones in the specified parameter.
 * Nodes marked as "deleted" will be removed from the record.
 * @param {object} nodes - The nodes to be added or updated.
 * @param {boolean} [removeFlags] - True if flags like "deleted" or "created" must be removed from the nodes, false otherwise.
 * @returns {object} - The updated record.
 */
export const mergeNodes =
  (nodes, { removeFlags = false, removeDirtyFlag = true, sideEffect = false } = {}) =>
  (record) => {
    let recordUpdated = sideEffect ? record : { ...record }
    if (!(keys.nodes in recordUpdated)) {
      recordUpdated[keys.nodes] = {}
    }

    Object.entries(nodes).forEach(([nodeUuid, n]) => {
      // Remove deleted node
      if (Node.isDeleted(n)) {
        recordUpdated = deleteNode(n, { sideEffect })(recordUpdated)
      } else {
        const nodeExisting = RecordReader.getNodeByUuid(nodeUuid)(recordUpdated)
        // Exclude dirty nodes currently being edited by the user
        const toBeAdded =
          !nodeExisting || // NodeExisting does not exist, n is new node
          !Node.isDirty(nodeExisting) || // Existing node is not dirty
          Node.isDirty(n) || // New node is dirty, replace the existing one
          A.equals(Node.getValue(nodeExisting), Node.getValue(n)) || // New node is not dirty and has the same value of the existing (dirty) node
          (Node.isValueBlank(nodeExisting) && Node.isDefaultValueApplied(n)) // Existing node has a blank value and n has a default value applied

        if (toBeAdded) {
          // Remove "updated" and "created" flags (used by Survey RDB generation)
          const nodeUpdated = removeFlags ? Node.removeFlags({ sideEffect, removeDirtyFlag })(n) : n
          recordUpdated = Records.addNode(nodeUpdated, { sideEffect })(recordUpdated)
        }
      }
    })
    return recordUpdated
  }

export const assocNode =
  (node, { sideEffect = false } = {}) =>
  (record) => {
    if (Node.isDeleted(node)) {
      return deleteNode(node)(record)
    } else {
      return Records.addNode(node, { sideEffect })(record)
    }
  }

/**
 * Adds new nodes to the record.
 * Nodes shouldn't have been added previously to the record, so in this casa there is no need to check for duplicates.
 * @param {!object} params - The parameters.
 * @param {!object} [params.nodes] - The nodes to be added.
 * @param {boolean} [params.updateNodesIndex] - True if the nodes must be added to the index (slower), false otherwise (faster).
 * @param {boolean} [params.sideEffect] - True if the passed record object can be modified by calling this function (faster), false otherwise.
 * @returns {object} - The updated record.
 */
export const assocNodes =
  ({ nodes, updateNodesIndex = true, sideEffect = false }) =>
  (record) =>
    Records.addNodes(nodes, { updateNodesIndex, sideEffect })(record)

/**
 * Replaces all the nodes of a record, building the nodes index from scratch.
 * Use it instead of assocNodes with nodes already in the record: in that case the index would not be stored,
 * and every following node update would rebuild it entirely.
 * @param {!object} nodes - The nodes (indexed by uuid).
 * @returns {(record: object) => object} - Function returning the updated record.
 */
export const replaceNodes = (nodes) => (record) => {
  const recordWithoutNodes = { ...record }
  delete recordWithoutNodes[keys.nodes]
  delete recordWithoutNodes._nodesIndex
  return Records.addNodes(nodes, { sideEffect: true, sortNodes: true })(recordWithoutNodes)
}

export const mergeNodeValidations = (nodeValidations) => (record) =>
  A.pipe(Validation.getValidation, Validation.mergeValidation(nodeValidations), (validationMerged) =>
    Validation.assocValidation(validationMerged)(record)
  )(record)

export const dissocNodes = A.dissoc(keys.nodes)

export const assocDateModified = (dateModified) => (record) => {
  const dateModifiedString = Dates.formatForStorage(dateModified)
  return A.assoc(keys.dateModified, dateModifiedString)(record)
}

// ====== DELETE

export const deleteNode =
  (node, { sideEffect = false } = {}) =>
  (record) => {
    const nodeUuid = Node.getUuid(node)
    const { record: recordUpdated } = Records.removeNode(nodeUuid, { sideEffect })(record)
    return recordUpdated
  }
